import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, isMfaRequired, isDeviceVerifyRequired } from '../types/api';
import type { GoogleReturn, OidcProvider } from '../utils/googleReturn';
import apiService from '../services/api';
import Logger from '../utils/logger';
import { showErrorToast } from '../utils/toastUtils';
import { ErrorMessages } from '../constants/messages';

const MFA_TOKEN_KEY = 'nubitech-mfa-pending-token';
const DEVICE_VERIFY_TOKEN_KEY = 'nubitech-device-verify-pending-token';
const DEVICE_VERIFY_EMAIL_HINT_KEY = 'nubitech-device-verify-email-hint';
const GOOGLE_LINK_TOKEN_KEY = 'nubitech-google-link-pending-token';
const GOOGLE_LINK_EMAIL_HINT_KEY = 'nubitech-google-link-email-hint';
const OIDC_LINK_PROVIDER_KEY = 'nubitech-oidc-link-provider';

interface LoginResult {
  needsMfa: boolean;
  needsDeviceVerification: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /**
   * Sign in with email + password.
   * Returns `{needsMfa: true}` if the user has MFA enabled — the UI must then
   * show the TOTP page and call `completeMfaLogin(code)` to finish.
   * Returns `{needsDeviceVerification: true}` if the device is not trusted —
   * the UI must show the email verification page and call `completeDeviceVerification`.
   * Returns `{needsMfa: false, needsDeviceVerification: false}` when the cookie is posted directly.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  completeMfaLogin: (code: string) => Promise<void>;
  hasPendingMfa: () => boolean;
  cancelMfa: () => void;
  completeDeviceVerification: (code: string, remember: boolean) => Promise<void>;
  resendDeviceVerificationCode: () => Promise<void>;
  hasPendingDeviceVerification: () => boolean;
  getDeviceVerifyEmailHint: () => string | null;
  cancelDeviceVerification: () => void;
  /**
   * Connexion Google : navigation de premier niveau vers Google. Le retour
   * arrive sur /login/google#status=... et passe par `handleGoogleReturn`.
   */
  loginWithGoogle: (returnTo?: string) => Promise<void>;
  handleGoogleReturn: (ret: GoogleReturn) => Promise<void>;
  /** Connexion SSO Entra ID de l'organisation qui a verifie le domaine de l'adresse. */
  loginWithEntra: (organisationId: string, returnTo?: string, loginHint?: string) => Promise<void>;
  handleOidcReturn: (ret: GoogleReturn, provider: OidcProvider) => Promise<void>;
  getPendingLinkProvider: () => OidcProvider;
  /** Code recu par courriel pour lier Google a un compte existant. */
  completeGoogleLink: (code: string) => Promise<LoginResult>;
  resendGoogleLinkCode: () => Promise<void>;
  hasPendingGoogleLink: () => boolean;
  getGoogleLinkEmailHint: () => string | null;
  cancelGoogleLink: () => void;
  /**
   * Remet a jour l'utilisateur en memoire.
   *
   * L'argument existe pour EVITER UN ALLER-RETOUR : un appel qui rend deja la fiche a
   * jour -- l'acceptation des conditions, par exemple -- la passe ici au lieu de
   * declencher un second GET. Sans argument, on relit `/users/me`.
   */
  refreshUser: (next?: User) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (apiService.isAuthenticated()) {
        try {
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
        } catch (error) {
          Logger.error('Failed to fetch user data', error);
          await apiService.logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const result = await apiService.login({ email, mot_de_passe: password });
      if (isMfaRequired(result)) {
        sessionStorage.setItem(MFA_TOKEN_KEY, result.mfa_token);
        return { needsMfa: true, needsDeviceVerification: false };
      }
      if (isDeviceVerifyRequired(result)) {
        sessionStorage.setItem(DEVICE_VERIFY_TOKEN_KEY, result.verify_token);
        if (result.email_hint) {
          sessionStorage.setItem(DEVICE_VERIFY_EMAIL_HINT_KEY, result.email_hint);
        }
        return { needsMfa: false, needsDeviceVerification: true };
      }
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      return { needsMfa: false, needsDeviceVerification: false };
    } catch (error) {
      Logger.error('Login failed', error);
      showErrorToast(error, ErrorMessages.AUTH_INVALID_CREDENTIALS);
      throw error;
    }
  };

  const completeMfaLogin = async (code: string): Promise<void> => {
    const mfaToken = sessionStorage.getItem(MFA_TOKEN_KEY);
    if (!mfaToken) {
      throw new Error('No pending MFA token — start the login flow again.');
    }
    try {
      await apiService.completeMfaLogin(mfaToken, code);
      sessionStorage.removeItem(MFA_TOKEN_KEY);
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      Logger.error('MFA verification failed', error);
      throw error;
    }
  };

  const hasPendingMfa = (): boolean => Boolean(sessionStorage.getItem(MFA_TOKEN_KEY));

  const cancelMfa = (): void => {
    sessionStorage.removeItem(MFA_TOKEN_KEY);
  };

  const completeDeviceVerification = async (code: string, remember: boolean): Promise<void> => {
    const verifyToken = sessionStorage.getItem(DEVICE_VERIFY_TOKEN_KEY);
    if (!verifyToken) {
      throw new Error('No pending device verification token — start the login flow again.');
    }
    try {
      await apiService.verifyDevice(verifyToken, code, remember);
      sessionStorage.removeItem(DEVICE_VERIFY_TOKEN_KEY);
      sessionStorage.removeItem(DEVICE_VERIFY_EMAIL_HINT_KEY);
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      Logger.error('Device verification failed', error);
      throw error;
    }
  };

  const resendDeviceVerificationCode = async (): Promise<void> => {
    const verifyToken = sessionStorage.getItem(DEVICE_VERIFY_TOKEN_KEY);
    if (!verifyToken) {
      throw new Error('No pending device verification token — start the login flow again.');
    }
    const result = await apiService.resendDeviceCode(verifyToken);
    // Le nouveau verify_token remplace l'ancien — issue_email_code a
    // invalide les anciens cote serveur, seul le code du dernier email vaut.
    sessionStorage.setItem(DEVICE_VERIFY_TOKEN_KEY, result.verify_token);
    if (result.email_hint) {
      sessionStorage.setItem(DEVICE_VERIFY_EMAIL_HINT_KEY, result.email_hint);
    }
  };

  const hasPendingDeviceVerification = (): boolean =>
    Boolean(sessionStorage.getItem(DEVICE_VERIFY_TOKEN_KEY));

  const getDeviceVerifyEmailHint = (): string | null =>
    sessionStorage.getItem(DEVICE_VERIFY_EMAIL_HINT_KEY);

  const cancelDeviceVerification = (): void => {
    sessionStorage.removeItem(DEVICE_VERIFY_TOKEN_KEY);
    sessionStorage.removeItem(DEVICE_VERIFY_EMAIL_HINT_KEY);
  };

  // ----- Connexion Google ---------------------------------------------------

  const loginWithGoogle = async (returnTo?: string): Promise<void> => {
    const { authorization_url } = await apiService.getGoogleAuthorizeUrl(returnTo);
    window.location.href = authorization_url;
  };

  const loginWithEntra = async (organisationId: string, returnTo?: string, loginHint?: string): Promise<void> => {
    const { authorization_url } = await apiService.getEntraAuthorizeUrl(organisationId, returnTo, loginHint);
    window.location.href = authorization_url;
  };

  const handleOidcReturn = async (ret: GoogleReturn, provider: OidcProvider): Promise<void> => {
    if (ret.status === 'link') sessionStorage.setItem(OIDC_LINK_PROVIDER_KEY, provider);
    await handleGoogleReturn(ret);
  };

  const getPendingLinkProvider = (): OidcProvider =>
    (sessionStorage.getItem(OIDC_LINK_PROVIDER_KEY) === 'entra' ? 'entra' : 'google');

  const handleGoogleReturn = async (ret: GoogleReturn): Promise<void> => {
    switch (ret.status) {
      case 'ok': {
        // Le cookie est deja pose par l'API sur le 302 : on recharge l'utilisateur.
        apiService.markCookieSession();
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        return;
      }
      case 'mfa':
        sessionStorage.setItem(MFA_TOKEN_KEY, ret.token as string);
        return;
      case 'device':
        sessionStorage.setItem(DEVICE_VERIFY_TOKEN_KEY, ret.token as string);
        if (ret.email_hint) sessionStorage.setItem(DEVICE_VERIFY_EMAIL_HINT_KEY, ret.email_hint);
        return;
      case 'link':
        sessionStorage.setItem(GOOGLE_LINK_TOKEN_KEY, ret.token as string);
        if (ret.email_hint) sessionStorage.setItem(GOOGLE_LINK_EMAIL_HINT_KEY, ret.email_hint);
        return;
      default:
        return;
    }
  };

  const completeGoogleLink = async (code: string): Promise<LoginResult> => {
    const linkToken = sessionStorage.getItem(GOOGLE_LINK_TOKEN_KEY);
    if (!linkToken) {
      throw new Error('No pending Google link token — start the login flow again.');
    }
    const result = await apiService.confirmGoogleLink(linkToken, code);
    sessionStorage.removeItem(GOOGLE_LINK_TOKEN_KEY);
    sessionStorage.removeItem(GOOGLE_LINK_EMAIL_HINT_KEY);
    if (isMfaRequired(result)) {
      sessionStorage.setItem(MFA_TOKEN_KEY, result.mfa_token);
      return { needsMfa: true, needsDeviceVerification: false };
    }
    if (isDeviceVerifyRequired(result)) {
      sessionStorage.setItem(DEVICE_VERIFY_TOKEN_KEY, result.verify_token);
      if (result.email_hint) sessionStorage.setItem(DEVICE_VERIFY_EMAIL_HINT_KEY, result.email_hint);
      return { needsMfa: false, needsDeviceVerification: true };
    }
    const currentUser = await apiService.getCurrentUser();
    setUser(currentUser);
    return { needsMfa: false, needsDeviceVerification: false };
  };

  const resendGoogleLinkCode = async (): Promise<void> => {
    const linkToken = sessionStorage.getItem(GOOGLE_LINK_TOKEN_KEY);
    if (!linkToken) {
      throw new Error('No pending Google link token — start the login flow again.');
    }
    const result = await apiService.resendGoogleLinkCode(linkToken);
    // Nouveau code = nouveau jeton ; l'ancien est invalide cote serveur.
    sessionStorage.setItem(GOOGLE_LINK_TOKEN_KEY, result.link_token);
    if (result.email_hint) sessionStorage.setItem(GOOGLE_LINK_EMAIL_HINT_KEY, result.email_hint);
  };

  const hasPendingGoogleLink = (): boolean => Boolean(sessionStorage.getItem(GOOGLE_LINK_TOKEN_KEY));

  const getGoogleLinkEmailHint = (): string | null => sessionStorage.getItem(GOOGLE_LINK_EMAIL_HINT_KEY);

  const cancelGoogleLink = (): void => {
    sessionStorage.removeItem(GOOGLE_LINK_TOKEN_KEY);
    sessionStorage.removeItem(GOOGLE_LINK_EMAIL_HINT_KEY);
    sessionStorage.removeItem(OIDC_LINK_PROVIDER_KEY);
  };

  const logout = async () => {
    await apiService.logout();
    sessionStorage.removeItem(MFA_TOKEN_KEY);
    sessionStorage.removeItem(DEVICE_VERIFY_TOKEN_KEY);
    sessionStorage.removeItem(DEVICE_VERIFY_EMAIL_HINT_KEY);
    sessionStorage.removeItem(GOOGLE_LINK_TOKEN_KEY);
    sessionStorage.removeItem(GOOGLE_LINK_EMAIL_HINT_KEY);
    sessionStorage.removeItem(OIDC_LINK_PROVIDER_KEY);
    setUser(null);
  };

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      const updatedUser = await apiService.updateProfile(profileData);
      setUser(updatedUser);
    } catch (error) {
      Logger.error('Failed to update profile', error);
      showErrorToast(error, 'Echec de la mise a jour du profil');
      throw error;
    }
  };

  const refreshUser = async (next?: User): Promise<void> => {
    if (next) {
      setUser(next);
      return;
    }
    const currentUser = await apiService.getCurrentUser();
    setUser(currentUser);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    completeMfaLogin,
    hasPendingMfa,
    cancelMfa,
    completeDeviceVerification,
    resendDeviceVerificationCode,
    hasPendingDeviceVerification,
    getDeviceVerifyEmailHint,
    cancelDeviceVerification,
    loginWithGoogle,
    handleGoogleReturn,
    loginWithEntra,
    handleOidcReturn,
    getPendingLinkProvider,
    completeGoogleLink,
    resendGoogleLinkCode,
    hasPendingGoogleLink,
    getGoogleLinkEmailHint,
    cancelGoogleLink,
    logout,
    updateProfile,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
